import { useState } from 'react';
import { Heart, Shield, Phone, Mail, CheckCircle, Upload, Camera } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

interface VerificationScreenProps {
  onComplete: () => void;
}

export default function VerificationScreen({ onComplete }: VerificationScreenProps) {
  const [phoneOtp, setPhoneOtp] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  const handlePhoneVerify = () => {
    // Simulate OTP verification
    setPhoneVerified(true);
  };

  const handleEmailVerify = () => {
    // Simulate email verification
    setEmailVerified(true);
  };

  const handleSkip = () => {
    onComplete();
  };

  const handleContinue = () => {
    if (phoneVerified) {
      onComplete();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="size-8 text-red-600 fill-red-600" />
            <span className="text-2xl text-red-900">Atootbandan</span>
          </div>
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full mb-4">
            <Shield className="size-5" />
            <span>Verify Your Profile</span>
          </div>
          <h1 className="text-2xl mb-2">Profile Verification</h1>
          <p className="text-gray-600">Verified profiles get 10x more responses!</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <Tabs defaultValue="phone" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="phone">Phone</TabsTrigger>
              <TabsTrigger value="email">Email</TabsTrigger>
              <TabsTrigger value="document">Documents</TabsTrigger>
            </TabsList>

            {/* Phone Verification */}
            <TabsContent value="phone" className="space-y-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center size-16 bg-red-100 rounded-full mb-4">
                  <Phone className="size-8 text-red-600" />
                </div>
                <h3 className="text-xl mb-2">Verify Phone Number</h3>
                <p className="text-gray-600">We've sent a 6-digit OTP to +91 98765 43210</p>
              </div>

              {phoneVerified ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center size-16 bg-green-100 rounded-full mb-4">
                    <CheckCircle className="size-8 text-green-600" />
                  </div>
                  <h3 className="text-xl text-green-600 mb-2">Phone Verified!</h3>
                  <p className="text-gray-600">Your phone number has been successfully verified</p>
                </div>
              ) : (
                <>
                  <div>
                    <Label>Enter OTP</Label>
                    <Input
                      value={phoneOtp}
                      onChange={(e) => setPhoneOtp(e.target.value)}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      className="mt-2 text-center text-2xl tracking-widest"
                    />
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Didn't receive OTP?</span>
                    <button className="text-red-600 hover:underline">Resend OTP</button>
                  </div>

                  <Button
                    onClick={handlePhoneVerify}
                    className="w-full bg-red-600 hover:bg-red-700"
                    disabled={phoneOtp.length !== 6}
                  >
                    Verify Phone Number
                  </Button>
                </>
              )}
            </TabsContent>

            {/* Email Verification */}
            <TabsContent value="email" className="space-y-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center size-16 bg-red-100 rounded-full mb-4">
                  <Mail className="size-8 text-red-600" />
                </div>
                <h3 className="text-xl mb-2">Verify Email Address</h3>
                <p className="text-gray-600">We've sent a verification link to your email</p>
              </div>

              {emailVerified ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center size-16 bg-green-100 rounded-full mb-4">
                    <CheckCircle className="size-8 text-green-600" />
                  </div>
                  <h3 className="text-xl text-green-600 mb-2">Email Verified!</h3>
                  <p className="text-gray-600">Your email has been successfully verified</p>
                </div>
              ) : (
                <>
                  <div>
                    <Label>Enter Verification Code</Label>
                    <Input
                      value={emailOtp}
                      onChange={(e) => setEmailOtp(e.target.value)}
                      placeholder="Enter code from email"
                      className="mt-2"
                    />
                  </div>

                  <Button
                    onClick={handleEmailVerify}
                    className="w-full bg-red-600 hover:bg-red-700"
                  >
                    Verify Email
                  </Button>

                  <div className="text-center">
                    <button className="text-red-600 hover:underline text-sm">
                      Resend verification email
                    </button>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Document Verification */}
            <TabsContent value="document" className="space-y-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center size-16 bg-red-100 rounded-full mb-4">
                  <Shield className="size-8 text-red-600" />
                </div>
                <h3 className="text-xl mb-2">Document Verification</h3>
                <p className="text-gray-600">Upload government ID for complete verification</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Upload ID Proof (Aadhaar/PAN/Passport)</Label>
                  <div className="mt-3 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-500 transition-colors cursor-pointer">
                    <Upload className="size-10 mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-600">Click to upload ID document</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, JPG or PNG (Max 5MB)</p>
                  </div>
                </div>

                <div>
                  <Label>Upload Selfie with ID</Label>
                  <div className="mt-3 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-500 transition-colors cursor-pointer">
                    <Camera className="size-10 mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-600">Upload a selfie holding your ID</p>
                    <p className="text-xs text-gray-400 mt-1">JPG or PNG (Max 5MB)</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Why verify?</strong> Verified profiles get featured in search results and receive more responses. Your documents are kept 100% confidential.
                  </p>
                </div>

                <Button className="w-full bg-red-600 hover:bg-red-700">
                  Submit for Verification
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={handleSkip}
              className="flex-1"
            >
              Skip for Now
            </Button>
            <Button
              onClick={handleContinue}
              disabled={!phoneVerified}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              Continue to Dashboard
            </Button>
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm text-center">
            <CheckCircle className="size-8 mx-auto mb-2 text-green-600" />
            <p className="text-sm">10x More Responses</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm text-center">
            <Shield className="size-8 mx-auto mb-2 text-red-600" />
            <p className="text-sm">Trust Badge</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm text-center">
            <CheckCircle className="size-8 mx-auto mb-2 text-blue-600" />
            <p className="text-sm">Priority Listing</p>
          </div>
        </div>
      </div>
    </div>
  );
}
